import React, { MutableRefObject, ReactNode } from 'react'
import { ICourse2, ICurriculum } from '@et/types/Courses'
import _, { parseInt } from 'lodash'
import animateScrollTo from 'animated-scroll-to'
import { IGalleryImage, IGatsbyChildImageSharp } from '@et/types/Gatsby'
import { IGalleryImageQuery } from '@et/types/ImageTypes'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import { withPrefix } from 'gatsby-link'

/**
 * renderSvg(svg)
 * - Component Helper to render and svg item
 *
 * @param {Object} Svg - from importing an svg into react
 */

export const renderSvg = (Svg: string): ReactNode => {
  if (!Svg) {
    return (<div>No SVG found</div>)
  }
  return <Svg />
}

export function countVideos(curriculum: ICurriculum[]): number {
  return curriculum.reduce((acc: number, current: ICurriculum) => {
    return acc + current.videos.length
  }, 0)
}

export function countMins(curriculum: ICurriculum[]): string {
  let secondsLeft = curriculum
    .map((module) => module.videos.map((video) => {
      const [mins, secs] = video.duration.split(':').map(parseFloat)
      return (mins * 60) + secs
    }))
    .reduce((acc: number, current: any) => {
      return current.reduce((accumulator: number, currentValue: number) => accumulator + currentValue) + acc
    }, 0)
  const hours = Math.floor(secondsLeft / 3600)
  secondsLeft = secondsLeft % 3600
  const minutes = Math.floor(secondsLeft / 60)
  // secondsLeft = secondsLeft % 60
  // console.log(hours, minutes, secondsLeft)
  const percentLeft = Math.floor(minutes / 60 * 100)
  // console.log('percentLeft', percentLeft)
  if (percentLeft >= 90) {
    return `${hours + 1}`
  }
  return `Over ${hours}`
}

export interface IMeta {
  name: string,
  content: string,
}

export interface IOGType {
  property: string,
  content: string,
}

export const socialUtils = {
  twitter: {
    author: '@Teelac',
    url: 'https://twitter.com/teelacunningham',
    defaultImage: 'https://twitter.com/teelacunningham',
  },
}
export type IMetas = IOGType | IMeta
/**
 * * Tested
 * Twitter Card default
 * - Add-on and override default twitter props
 *
 * @param {IMeta} additionalProps
 * @returns Array
 *
 * TODO: DEPRECATED
 */
export const twitterDefaultMeta = (additionalProps: IMeta[] = []): IMeta[] => {
  const twitter: any = [
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:title`,
      content: `${process.env.GATSBY_TITLE}`,
    },
    {
      name: `twitter:description`,
      content: `${process.env.GATSBY_DESCRIPTION}`,
    },
    {
      name: `twitter:creator`,
      content: `${socialUtils.twitter.author}`,
    },
    {
      name: `twitter:site	`,
      content: `${socialUtils.twitter.url}`,
    },
    {
      name: `twitter:url`,
      content: `${socialUtils.twitter.url}`,
    },
  ]
  return _.unionBy(additionalProps, twitter, 'name')
}

interface ITwitterMetaProps {
  course?: ICourse2 | undefined
  siteUrl: string
  url?: string
  socialImg: string
  singlePage?: {
    title: string
    tagline: string
    slug: string
  }
}

export const twitterMeta = ({ course, siteUrl, socialImg, singlePage }: ITwitterMetaProps): IMeta[] => {
  const twitter: any = [
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:title`,
      content: `${process.env.GATSBY_TITLE}`,
    },
    {
      name: `twitter:description`,
      content: `${process.env.GATSBY_DESCRIPTION}`,
    },
    {
      name: `twitter:creator`,
      content: `${socialUtils.twitter.author}`,
    },
    {
      name: `twitter:site	`,
      content: `${socialUtils.twitter.url}`,
    },
    {
      name: `twitter:url`,
      content: `${socialUtils.twitter.url}`,
    },
  ]

  if (course) {
    return _.unionBy(createTwitterPageMeta({
      title: course.title,
      tagline: course.tagline,
      siteUrl,
      imgSrc: socialImg,
    }), twitter, 'name')
  }

  if (singlePage) {
    return _.unionBy(createTwitterPageMeta({
      title: singlePage.title,
      tagline: singlePage.tagline,
      siteUrl,
      imgSrc: socialImg,
    }), twitter, 'name')
  }

  return twitter
}

export const facebookMeta = ({ course, siteUrl, socialImg, singlePage, url }: ITwitterMetaProps): IOGType[] => {
  const facebook: any = [
    {
      property: 'og:locale',
      content: 'en_US',
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:title`,
      content: `${process.env.GATSBY_TITLE}`,
    },
    {
      property: `og:description`,
      content: `${process.env.GATSBY_DESCRIPTION}`,
    },
    {
      property: 'og:site_name',
      content: `Every-Tuesday Courses`,
    },
    {
      property: `og:url`,
      content: url ? url : `${process.env.GATSBY_DB}`,
    },
  ]

  if (course) {
    return _.unionBy(createFacebookPageMeta({
      title: course.title,
      tagline: course.tagline,
      siteUrl,
      slug: course.slug,
      imgSrc: socialImg,
    }), facebook, 'property')
  }

  if (singlePage) {
    return _.unionBy(createFacebookPageMeta({
      title: singlePage.title,
      tagline: singlePage.tagline,
      siteUrl,
      slug: singlePage.slug,
      imgSrc: socialImg,
    }), facebook, 'property')
  }

  return facebook
}

/**
 * * Tested
 * Facebook Card default
 * - Add-on and override default props
 *
 * @param {IMeta} additionalProps
 * @returns Array
 */
export const facebookDefaultMeta = (additionalProps: IOGType[] = []): IOGType[] => {
  const facebook: any = [
    {
      property: 'og:locale',
      content: 'en_US',
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:title`,
      content: `${process.env.GATSBY_TITLE}`,
    },
    {
      property: `og:description`,
      content: `${process.env.GATSBY_DESCRIPTION}`,
    },
    {
      property: 'og:site_name',
      content: `Every-Tuesday Courses`,
    },
    {
      property: `og:url`,
      content: `${process.env.GATSBY_DB}`,
    },
  ]
  return _.unionBy(additionalProps, facebook, 'property')
}

interface ITwitterProps {
  title: string
  tagline: string
  siteUrl: string
  imgSrc: string
}

export const createTwitterPageMeta = ({ title, tagline, siteUrl, imgSrc }: ITwitterProps): IMeta[] => {
  return [
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:title`,
      content: `${title}`,
    },
    {
      name: `twitter:description`,
      content: `${tagline}`,
    },
    {
      name: `twitter:image`,
      content: `${imgSrc}`,
    },
  ]
}

interface IFacebookProps {
  title: string
  tagline: string
  siteUrl: string
  slug: string
  imgSrc: string
}

export const createFacebookPageMeta = ({ title, tagline, siteUrl, slug, imgSrc }: IFacebookProps): IOGType[] => {
  return [
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: tagline,
    },
    {
      property: 'og:site_name',
      content: `Every-Tuesday Courses: ${title}`,
    },
    {
      property: `og:url`,
      content: `${siteUrl}/${slug}`,
    },
    {
      property: 'og:image',
      content: `${imgSrc}`,
    },
    {
      property: 'og:image:secure_url',
      content: `${imgSrc}`,
    },
    {
      property: 'og:image:alt',
      content: `${tagline}`,
    },
    {
      property: 'og:image:type',
      content: ' image/jpeg',
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '645',
    },
  ]
}

export const scrollPageTo = (elementRef: MutableRefObject<null | HTMLElement>, offset = -75) => {
  if (elementRef.current) {
    // @ts-ignore
    animateScrollTo(elementRef.current, {
      // duration of the scroll per 1000px, default 500
      speed: 250,

      // minimum duration of the scroll
      minDuration: 250,

      // maximum duration of the scroll
      maxDuration: 500,

      // DOM element to scroll, default window
      // Pass a reference to a DOM object
      // Example: document.querySelector('#element-to-scroll'),
      element: window,

      // Additional offset value that gets added to the desiredOffset.  This is
      // useful when passing a DOM object as the desiredOffset and wanting to adjust
      // for an fixed nav or to add some padding.
      verticalOffset: offset,

      // should animated scroll be canceled on user scroll/keypress
      // if set to "false" user input will be disabled until animated scroll is complete
      // (when set to false, "passive" will be also set to "false" to prevent Chrome errors)
      cancelOnUserAction: true,

      // Set passive event Listeners to be true by default. Stops Chrome from complaining.
      passive: true,

      // Scroll horizontally rather than vertically (which is the default)
      // horizontal: true,

      // function that will be executed when the scroll animation is finished
      // onComplete: function() {});
    })
  }
}

/**
 * Gatsby Image Filter helper
 * - Helps reduce an array of gatsby images, and return the one with a matching filename
 * - IMPORTANT that the graphql return the NAME on the node to parse
 *
 * @param {string} fileName
 * @param {array} data
 * @returns Array
 */
type FluidImageCombo = IGatsbyChildImageSharp & IGalleryImage

export function getFluidImageByName(fileName: string, data: any): FluidImageCombo {

  return data.reduce((acc: any, brush: any) => {

    if (brush.node.name === fileName) {
      return brush.node
    }

    return acc
  }, {})
}

/**
 * Gatsby Image Filter helper
 * - Helps reduce an array of gatsby images, and return the one with a matching filename
 * - IMPORTANT that the graphql return the NAME on the node to parse
 *
 * @param {string} fileName
 * @param {array} data
 * @returns Array
 */
interface IGalleryImageV3 {
  fullSize: {
    gatsbyImageData: IGatsbyImageData
  },
  id: string
  name: string
  thumbnail: {
    gatsbyImageData: IGatsbyImageData
  },
}

export function getGatsbyDataImageByName(fileName: string, data: any): IGalleryImageV3 {

  return data.reduce((acc: any, brush: any) => {

    if (brush.node.name === fileName) {
      return brush.node
    }

    return acc
  }, {})
}

export const orderImgQueryByNumber = (data: IGalleryImageQuery[]) => {
  return data.sort((a, b) => {
    const nameA = a.node.name.split('-').pop() || '0'
    const nameB = b.node.name.split('-').pop() || '0'

    if (nameB === 'template') {
      return 0
    }

    return parseInt(nameA, 10) - parseInt(nameB, 10)
  })
}

export const filterNodesByName = (arr: IGalleryImageQuery[], name: string): any => {
  return arr.reduce((acc, item) => {
    if (item.node.name === name) {
      acc = item
    }
    return acc
  }, {})
}

export function renderSentinel() {
  return (
    <style type='text/css'>{`
								@font-face {
									font-family: "Sentinel Black";
									font-style: normal;
									font-weight: 900;
									src: url(/fonts/sentinel/Sentinel-Black.woff2) format("woff2");
									font-display: swap;
									}
									
									@font-face {
									font-family: "Sentinel MediumItal";
									font-style: italic;
									font-weight: 500;
									src: url(/fonts/sentinel/Sentinel-MediumItal.woff2) format("woff2");
									font-display: swap;
									}
									@font-face {
									font-family: "Sentinel SemiboldItal";
									font-style: italic;
									font-weight: 500;
									src: url(/fonts/sentinel/Sentinel-SemiboldItal.woff2) format("woff2");
									font-display: swap;
									}
									@font-face {
									font-family: "Sentinel";
									font-style: normal;
									font-weight: 600;
									src: url(/fonts/sentinel/Sentinel-Semibold.woff2) format("woff2");
									font-display: swap;
									}
						`}</style>
  )
}

export function renderMissMagnolia() {
  const testEnv = process.env.NODE_ENV === 'test'
  const MissMagnoliaEot = testEnv ? '' : withPrefix('/fonts/missMagnolia/MissMagnolia-Regular.eot')
  const MissMagnoliawoff = testEnv ? '' : withPrefix('/fonts/missMagnolia/MissMagnolia-Regular.woff')
  const MissMagnoliaWoff2 = testEnv ? '' : withPrefix('/fonts/missMagnolia/MissMagnolia-Regular.woff2')

  return (
    <style type='text/css'>{`
      @font-face {
      font-family: 'miss-magnolia';
      src: url(${MissMagnoliaEot}), 
      url(${MissMagnoliawoff}) format("woff2"), 
      url(${MissMagnoliaWoff2}) format("woff");
      font-weight: normal;
      font-style: normal
    }             
    `}</style>
  )
}

export function isHtml(input: string): boolean {
  return /<[a-z]+\d?(\s+[\w-]+=("[^"]*"|'[^']*'))*\s*\/?>|&#?\w+;/i.test(input);
}

export function calculateSaleSavingsPercentage(price: string, salePrice: string): number {
  const originalPrice = parseInt(price, 10)
  const costDifference = originalPrice - parseInt(salePrice, 10)
  const priceDifference = costDifference / originalPrice
  return Math.round(priceDifference * 100)
}

export function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ')
}
export const consoleColors = {
  yellow: '#ffd321',
  purple: '#7f21ff',
  magenta: '#e0005a',
  orange: '#ec9600',
}
export const consoleHelper = (data: string, obj: any = null, location?: String | null, color: {
  bg: string, text: string
} = { bg: "#21ff7f", text: "#000" }) => {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') return;

  console.group(
    `%c${data}`,
    `background-color: ${color.bg} ; color: ${color.text} ; font-weight: bold ; padding: 4px ;`);

  obj
    ? console.log(data, obj)
    : console.log(data)

  location && console.log(`%cLocation: 👉 ${location}`, `color: #626262 ; font-weight: 300 ;`);

  console.groupEnd();
  console.log('',);

}
